import React, { useMemo, useEffect, useState } from "react";

// MUI-DataGrid
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";

// MUI
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Components
import Loader from "../../loader/Loader";

// Hooks
import {
  useAppSelector,
  useAppDispatch,
} from "../../../redux/hooks/redux-hooks";

// Utils
import { isValidArr } from "../../../utility/utils";

// Config
import { appConfig } from "../../../utility/config";
import ReportingColumns from "./ProjectReportColumnsData";

// Services
import ProjectService from "../../../services/ProjectService";

// Model
import { ProjectReportResponseModel } from "../../../models/ProjectReportModel";

type FieldValueType = string | number | boolean | Date | null;

// Constants
const projectDetailUnnecessaryColmuns = [
  "customerGreenClassification",
  "isActive",
  "modifiedBy",
  "modifiedDate",
  "projectStatusId",
  "savedAsDraftByUserID",
  "draftedByUserID",
  "submittedByUserID",
  "buApproverUserID",
  "sbuApproverUserID",
  "sbgApproverUserID",
  "csgoApproverUserID",
  "rejectedUserId",
  "rejectedComment",
  "isCompleted",
];
const yesOrNoColumns = [
  "isGreenListCustomer",
  "isBaseLoad",
  "isNewProcessApplicable",
  "isJvpartnerRequired",
  "isTechPartnerRequired",
  "isOM",
  "isLicenseRequired",
  "isNewTeprocessCommenced",
  "isWorkStarted",
  "bidExploratoryStatus",
  "isBidDropped",
  "isSavedAsDraft",
  "isDrafted",
  "isSubmitted",
  "isBuapproved",
  "isBuonHold",
  "isSbuapproved",
  "isSbuonHold",
  "isSbgapproved",
  "isSbgonHold",
  "isCsgoapproved",
  "isCsgoonHold",
  "isRejected",
];

const viewOptions = [
  {
    label: "Pipeline",
    value: "pipeline",
  },
  {
    label: "Go No Go",
    value: "gonogo",
  },
  {
    label: "Win Loss",
    value: "winloss",
  },
];

const viewColumns: { [key: string]: string[] } = {
  pipeline: [
    "projectId",
    "projectName",
    "sbu",
    "segmentLob",
    "segmentClassification",
    "estimatedValue",
    "isPriceVariation",
    "isTechPartnerRequired",
    "tenderExpectedDate",
    "state",
    "city",
    "contractType",
    "customerName",
    "projectOverallWinLossStatus",
    "bidSubmissionDate",
    "isBidDropped",
    "isSubmitted",
    "isSbgapproved",
    "isCsgoapproved",
    "isRejected"
  ],
  gonogo: [
    "projectId",
    "projectName",
    "sbu",
    "segmentLob",
    "segmentClassification",
    "isGreenListCustomer",
    "estimatedValue",
    "isNewProcessApplicable",
    "preBidExpenses",
    "isTechPartnerRequired",
    "goFactorPercentage",
    "getFactorPercentage",
    "winProbabilityPercentage",
    "bidSubmissionDate",
    "country",
    "state",
    "city",
    "bidManagerName",
    "isNewTeprocessCommenced",
    "isWorkStarted",
    "isSubmitted",
    "isBUApproved",
    "isSBUApproved",
    "isSBGApproved",
    "isCSGOApproved",
  ],
  winloss: [
    "projectId",
    "projectName",
    "sbu",
    "segmentLob",
    "segmentClassification",
    "isGreenListCustomer",
    "estimatedValue",
    "isNewProcessApplicable",
    "preBidExpenses",
    "bidManagerName",
    "submittedDate",
    "sbuapprovedDate",
    "sbgapprovedDate",
    "csgoapprovedDate",
    "projectOverallWinLossStatus",
  ],
};

const bidderDetailsObj = {
  rank: "rank_",
  bidderName: "bidderName_",
  evaluatedPrice: "evaluatedPrice_",
  remarks: "remarks_",
};

type CustomToolBarProps = {
  view: string;
  setView: React.Dispatch<React.SetStateAction<string>>;
};

const CustomToolBar = ({ view, setView }: CustomToolBarProps) => {
  const viewArr = view.split(",");
  const viewValue = viewOptions.filter((rec) => viewArr.includes(rec.value));

  // To change the columns visibile in the table
  const viewChangeHandler = (
    event: React.SyntheticEvent,
    value: {
      label: string;
      value: string;
    }[]
  ) => {
    const viewString = value.map((rec) => rec.value).join(",");
    setView(viewString);
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport
        printOptions={{
          disableToolbarButton: true,
        }}
      />
      <Autocomplete
        multiple
        size="small"
        sx={{ minWidth: "150px" }}
        options={viewOptions}
        value={viewValue}
        onChange={viewChangeHandler}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="Select view" />
        )}
      />
    </GridToolbarContainer>
  );
};

const ProjectReport = () => {
  const dispatch = useAppDispatch();

  const [isFetching, setIsFetching] = useState(false);
  const [allProjects, setAllProjects] =
    useState<ProjectReportResponseModel | null>(null);
  const [view, setView] = useState(viewOptions[0].value);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  // To get logged in user details
  const { userId, roleId } = useAppSelector((state) => ({
    userId: state.user.loggedIn_User.userId,
    roleId: state.user.loggedIn_User.roleId,
  }));

  // To prepare the table columns
  const columns = useMemo(() => {
    const projectColumns: GridColDef[] = [...ReportingColumns];

    // To get column names for bidder details
    function getColumnsProps(i: number) {
      return [
        {
          field: `${bidderDetailsObj.rank}${i}`,
          headerName: `Rank ${i}`,
          type: "string",
          width: 100,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
        {
          field: `${bidderDetailsObj.bidderName}${i}`,
          headerName: `Bidder Name ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
        {
          field: `${bidderDetailsObj.evaluatedPrice}${i}`,
          headerName: `Evaluated Price ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
        {
          field: `${bidderDetailsObj.remarks}${i}`,
          headerName: `Remarks ${i}`,
          type: "string",
          width: 150,
          valueGetter: ({ value }: any) => {
            return value || "";
          },
        },
      ];
    }

    if (allProjects?.maxBidderDetailCount) {
      for (let i = 1; i <= allProjects.maxBidderDetailCount; i++) {
        const bidderDetailFieldProps = getColumnsProps(i);
        projectColumns.push(...bidderDetailFieldProps);
      }
    }

    return projectColumns;
  }, [allProjects?.maxBidderDetailCount]);

  // To get columns keys array
  const columnKeys = useMemo(() => {
    if (isValidArr(columns)) {
      return columns.map((rec) => rec.field);
    }
    return [];
  }, [columns]);

  // To prepare the table data
  const rows = useMemo(() => {
    const projectRows: any[] = [];

    function getRowValue(key: string, value: FieldValueType) {
      if (yesOrNoColumns.includes(key)) {
        return value === true ? "Yes" : value === false ? "No" : "";
      }
      return value;
    }

    if (isValidArr(allProjects?.allProjectsReportingModuleDetails)) {
      allProjects?.allProjectsReportingModuleDetails.forEach((project) => {
        const projectObj: { [key: string]: FieldValueType } = {}; // To store the project information as object

        // To get the project details information
        Object.entries(project.projectDetails).forEach(([key, value]) => {
          // Ignore unnecessary columns
          if (projectDetailUnnecessaryColmuns.includes(key)) {
            return;
          }
          projectObj[key] = getRowValue(key, value);
        });

        // To get the project bidder details information
        if (isValidArr(project.projectBidderDetails)) {
          project.projectBidderDetails.forEach((bidderDetails, i) => {
            const index = i + 1;
            projectObj[`${bidderDetailsObj.rank}${index}`] = bidderDetails.rank;
            projectObj[`${bidderDetailsObj.bidderName}${index}`] =
              bidderDetails.bidderName;
            projectObj[`${bidderDetailsObj.evaluatedPrice}${index}`] =
              bidderDetails.evaluatedPrice;
            projectObj[`${bidderDetailsObj.remarks}${index}`] =
              bidderDetails.remarks;
          });
        }

        projectRows.push(projectObj); // Push project object information into row array
      });
    }

    return projectRows;
  }, [allProjects?.allProjectsReportingModuleDetails]);

  // To fetch project details
  useEffect(() => {
    async function getProjectsData(userId: number, roleId: number) {
      try {
        setIsFetching(true);
        const response = await ProjectService.fetchUserProjectsReportData(
          userId,
          roleId
        );
        setAllProjects(response);
        setIsFetching(false);
      } catch (error) {
        console.log(error);
      }
    }

    getProjectsData(userId, roleId);
  }, [dispatch, userId, roleId]);

  // To selet the column visibility options based on view
  useEffect(() => {
    const viewArr = view.split(",");
    let viewColumnsArr: string[] = [];
    let hideColumns: GridColumnVisibilityModel = {};
    if (isValidArr(viewArr)) {
      viewArr.forEach((rec) => {
        if (viewColumns[rec]) {
          viewColumnsArr.push(...viewColumns[rec]);
        }
        if (rec === "winloss" && allProjects?.maxBidderDetailCount) {
          for (let i = 1; i <= allProjects.maxBidderDetailCount; i++) {
            viewColumnsArr.push(
              bidderDetailsObj.rank + i,
              bidderDetailsObj.bidderName + i,
              bidderDetailsObj.evaluatedPrice + i,
              bidderDetailsObj.remarks + i
            );
          }
        }
      });
      if (isValidArr(viewColumnsArr)) {
        viewColumnsArr = [...new Set(viewColumnsArr)];
      }
    }

    if (isValidArr(viewColumnsArr)) {
      columnKeys.forEach((key) => {
        if (!viewColumnsArr.includes(key)) {
          hideColumns[key] = false;
        }
      });
    }
    setColumnVisibilityModel(hideColumns);
  }, [view, columnKeys, allProjects?.maxBidderDetailCount]);

  return (
    <Box
      width="100vw"
      height={`calc(100vh - ${appConfig.headerHeight + appConfig.dashboardStackHeight
        }px)`}
    >
      {isFetching ? <Loader /> : null}
      <DataGrid
        rows={rows}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        slots={{
          toolbar: CustomToolBar,
        }}
        slotProps={{
          toolbar: {
            view: view,
            setView: setView,
          },
          panel: {
            sx: {
              '& .MuiDataGrid-columnsPanel': {
                maxHeight: '120px',
              }
            },
          },
        }}
        getRowId={(row) => row.projectId}
        getEstimatedRowHeight={() => 100}
        getRowHeight={() => "auto"}
        sx={{
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "5px",
          },
        }}
      />
    </Box>
  );
};

export default ProjectReport;
